import { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box,
    Chip,
    Divider,
    Grid,
    IconButton,
    Link,
    styled,
    TableBody,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import dayjs from 'dayjs';

import DialogWrapper from '@libComponents/Dialog';
import { Button } from '@libComponents';
import TableContainer from '@libComponents/table/TableContainer';
import TableHeaderContainer from '@libComponents/table/TableHeaderContainer';
import TableCellContainer from '@libComponents/table/TableCellContainer';
import { secureFileViewerLink } from 'employee/utils/employeeExportApi';

import { STATUS } from '../common';
import { groupChanges } from '../changeGroups';
import { reviewChangeRequest } from '../redux.actions';
import StatusChip from './StatusChip';

/**
 * Stored file paths are saved as an absolute URL against the file base env
 * (e.g. http://localhost:3001/file/private/…), which may not match the origin
 * the API is actually served from. Reduce it to the API-relative `file/private/…`
 * part so the viewer resolves it against the real API base in every environment.
 */
const toApiRelativePath = (stored) => {
    if (!stored) return '';
    const match = String(stored).match(/file\/(?:private|public)\/.+$/);
    return match ? match[0] : stored;
};

/** Opens a stored private document/image in the secure in-app file viewer
 *  (fetches it through the API with the reviewer's own credentials). */
const openFile = (path) => {
    const href = secureFileViewerLink(toApiRelativePath(path));
    if (href) window.open(href, '_blank', 'noopener,noreferrer');
};

/** Renders a projected list-entry cell: a stored file (shape {__file,name})
 *  becomes a clickable viewer link, everything else is plain text. */
const renderEntryCell = (value) => {
    if (value && typeof value === 'object' && value.__file) {
        return <FileValue path={value.__file} text={value.name} fallback='—' />;
    }
    return value ? value : '—';
};

/** A value cell that becomes a clickable link when it points at a stored file. */
const FileValue = ({ path, text, fallback }) => {
    if (path) {
        return (
            <Link
                component='button'
                type='button'
                underline='hover'
                onClick={() => openFile(path)}
                sx={{
                    textAlign: 'left',
                    fontWeight: 'inherit',
                    color: 'primary.main',
                    p: 0,
                    lineHeight: 1.4,
                    wordBreak: 'break-all'
                }}
            >
                {text || 'View file'}
            </Link>
        );
    }
    return text || fallback;
};

/**
 * Table surfaces. In light mode the palette's white/secondary already layer
 * nicely (card lighter than the panel behind it). In dark mode the palette's
 * "surface" is actually DARKER than the panel, so tables recede — here we pick
 * genuinely lighter greys so a nested card still pops above its panel, matching
 * the light-mode look the reviewer sees.
 */
const surfaces = (theme) => {
    const dark = theme.palette.mode === 'dark';
    return {
        border: dark ? '#3A3A3A' : theme.palette.border?.secondary || theme.palette.divider,
        headerBg: dark ? '#2E2E2E' : theme.palette.background.secondary,
        cardBg: dark ? '#262626' : theme.palette.background.white,
        rowHover: dark ? 'rgba(255,255,255,0.06)' : theme.palette.action.hover
    };
};

/**
 * Same table shell the employee-list / admin-list pages use: full width,
 * scrollable, fixed column layout, a banded header row and subtle row hover —
 * scoped with `> table` so nested entry tables keep their own styling.
 */
const TableBox = styled(Box)(({ theme }) => {
    const s = surfaces(theme);
    return {
        width: '100%',
        overflowX: 'auto',
        border: `1px solid ${s.border}`,
        borderRadius: theme.shape.borderRadius,
        '& > div > table': { tableLayout: 'fixed' },
        '& > div > table > thead .MuiTableCell-root': {
            backgroundColor: s.headerBg,
            fontWeight: 'bold'
        },
        '& > div > table > tbody > tr': {
            transition: 'background-color 0.2s ease',
            '&:hover': { backgroundColor: s.rowHover }
        }
    };
});

const formatScalar = (change, value) => {
    if (value === null || value === undefined || value === '') return null;
    if (change.type === 'date') {
        const date = dayjs(value);
        if (date.isValid()) return date.format('MMM DD, YYYY');
    }
    return String(value);
};

/**
 * Renders a list change as ONE table per entry (current vs requested paired by
 * position). Each table has three columns — Field / Current Value / Requested
 * Value — and one row per field of that entry.
 */
const EntryComparison = ({ oldItems = [], newItems = [] }) => {
    const count = Math.max(oldItems.length, newItems.length);
    if (!count) {
        return (
            <Box
                sx={{
                    py: 1.5,
                    px: 1.5,
                    border: (theme) => `1px dashed ${surfaces(theme).border}`,
                    borderRadius: 1
                }}
            >
                <Typography variant='body2' color='text.secondary'>
                    No entries to compare.
                </Typography>
            </Box>
        );
    }

    // Union of field keys across every entry, keeping first-seen order.
    const columns = [];
    [...oldItems, ...newItems].forEach((item) => {
        Object.keys(item || {}).forEach((key) => {
            if (!columns.includes(key)) columns.push(key);
        });
    });

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Array.from({ length: count }).map((_, index) => {
                const oldItem = oldItems[index] || {};
                const newItem = newItems[index] || {};
                return (
                    <Box key={index}>
                        <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}
                        >
                            {`Entry ${index + 1}`}
                        </Typography>
                        <Box
                            sx={{
                                width: '100%',
                                overflowX: 'auto',
                                border: (theme) => `1px solid ${surfaces(theme).border}`,
                                borderRadius: 1,
                                backgroundColor: (theme) => surfaces(theme).cardBg,
                                '& thead .MuiTableCell-root': {
                                    backgroundColor: (theme) => surfaces(theme).headerBg,
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                    py: 0.75
                                },
                                '& tbody .MuiTableCell-root': { py: 0.75 }
                            }}
                        >
                            <TableContainer sx={{ boxShadow: 'none' }}>
                                <TableHeaderContainer>
                                    <TableCellContainer typography='caption' sx={{ width: '34%' }}>
                                        Field
                                    </TableCellContainer>
                                    <TableCellContainer typography='caption' sx={{ width: '33%' }}>
                                        Current Value
                                    </TableCellContainer>
                                    <TableCellContainer typography='caption' sx={{ width: '33%' }}>
                                        Requested Value
                                    </TableCellContainer>
                                </TableHeaderContainer>
                                <TableBody>
                                    {columns.map((col) => (
                                        <TableRow key={col}>
                                            <TableCellContainer sx={{ fontWeight: 600 }}>
                                                {col}
                                            </TableCellContainer>
                                            <TableCellContainer sx={{ color: 'text.secondary' }}>
                                                {renderEntryCell(oldItem[col])}
                                            </TableCellContainer>
                                            <TableCellContainer>
                                                {renderEntryCell(newItem[col])}
                                            </TableCellContainer>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </TableContainer>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

/** Field / Current Value / Requested Value table for a group's scalar & file
 *  members (used by scalar, address and section groups). File members render
 *  as clickable viewer links. */
const ScalarTable = ({ items }) => (
    <Box
        sx={{
            width: '100%',
            overflowX: 'auto',
            border: (theme) => `1px solid ${surfaces(theme).border}`,
            borderRadius: 1,
            backgroundColor: (theme) => surfaces(theme).cardBg,
            '& thead .MuiTableCell-root': {
                backgroundColor: (theme) => surfaces(theme).headerBg,
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                py: 0.75
            },
            '& tbody .MuiTableCell-root': { py: 0.75 }
        }}
    >
        <TableContainer sx={{ boxShadow: 'none' }}>
            <TableHeaderContainer>
                <TableCellContainer typography='caption' sx={{ width: '34%' }}>Field</TableCellContainer>
                <TableCellContainer typography='caption' sx={{ width: '33%' }}>Current Value</TableCellContainer>
                <TableCellContainer typography='caption' sx={{ width: '33%' }}>Requested Value</TableCellContainer>
            </TableHeaderContainer>
            <TableBody>
                {items.map((change) => {
                    const oldText = formatScalar(change, change.oldValue);
                    const newText = formatScalar(change, change.newValue);
                    return (
                        <TableRow key={change.field}>
                            <TableCellContainer sx={{ fontWeight: 600 }}>{change.label}</TableCellContainer>
                            <TableCellContainer sx={{ color: 'text.secondary' }}>
                                {change.type === 'file'
                                    ? <FileValue path={change.oldFile} text={oldText} fallback='N/A' />
                                    : oldText || 'N/A'}
                            </TableCellContainer>
                            <TableCellContainer>
                                {change.type === 'file'
                                    ? <FileValue path={change.newFile} text={newText} fallback='(cleared)' />
                                    : newText || '(cleared)'}
                            </TableCellContainer>
                        </TableRow>
                    );
                })}
            </TableBody>
        </TableContainer>
    </Box>
);

/** Green Approve / red Reject toggle — one decision per group. */
const DecisionToggle = ({ value, onChange }) => (
    <ToggleButtonGroup
        size='small'
        exclusive
        value={value || null}
        onChange={(event, next) => {
            if (next) onChange(next);
        }}
        sx={{
            '& .MuiToggleButton-root': {
                textTransform: 'none',
                px: 1.5,
                py: 0.25,
                fontSize: '0.75rem',
                fontWeight: 600
            }
        }}
    >
        <ToggleButton
            value={STATUS.approved}
            sx={{ '&.Mui-selected': { backgroundColor: 'success.main', color: '#fff', '&:hover': { backgroundColor: 'success.dark' } } }}
        >
            Approve
        </ToggleButton>
        <ToggleButton
            value={STATUS.rejected}
            sx={{ '&.Mui-selected': { backgroundColor: 'error.main', color: '#fff', '&:hover': { backgroundColor: 'error.dark' } } }}
        >
            Reject
        </ToggleButton>
    </ToggleButtonGroup>
);

/** The expanded detail for a group: per-entry tables for lists, labelled
 *  sub-tables for address/section groups, or a single scalar table. */
const GroupDetail = ({ group }) => {
    if (group.type === 'list') {
        const change = group.members[0] || {};
        return <EntryComparison oldItems={change.oldItems} newItems={change.newItems} />;
    }
    if (group.sections && group.sections.length) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {group.sections.map((section) => (
                    <Box key={section.title}>
                        <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}
                        >
                            {section.title}
                        </Typography>
                        <ScalarTable items={section.members} />
                    </Box>
                ))}
            </Box>
        );
    }
    return <ScalarTable items={group.members} />;
};

/** Overall status of a group from its members' stored per-field statuses
 *  (used in read-only/history view). Only a genuine MIX of approved and
 *  rejected is "partially approved" — an all-pending group is still pending. */
const groupStatus = (members = []) => {
    const statuses = members.map((m) => m.status);
    if (!statuses.length) return STATUS.pending;
    if (statuses.every((s) => s === STATUS.approved)) return STATUS.approved;
    if (statuses.every((s) => s === STATUS.rejected)) return STATUS.rejected;
    if (statuses.every((s) => s === STATUS.pending)) return STATUS.pending;
    const hasApproved = statuses.some((s) => s === STATUS.approved);
    const hasRejected = statuses.some((s) => s === STATUS.rejected);
    // Approved + rejected together is the only true "partially approved" case;
    // anything else still awaiting a decision reads as pending.
    return hasApproved && hasRejected ? STATUS.partiallyApproved : STATUS.pending;
};

const InfoItem = ({ label, value }) => (
    <Grid item xs={12} sm={4}>
        <Typography variant='caption' color='text.secondary'>
            {label}
        </Typography>
        <Typography variant='body2'>{value || 'N/A'}</Typography>
    </Grid>
);

/**
 * Change-request details.
 *   mode='view'   read-only (employee history / decided requests)
 *   mode='review' admin accepts/rejects EACH field individually, then submits.
 * List changes (nominees, academic, ...) are expandable to their actual
 * current vs requested entries.
 */
const ChangeRequestDetailsDialog = ({
    open,
    onClose,
    request,
    mode = 'view',
    onReviewed,
    canCancel = false,
    onCancel
}) => {
    const dispatch = useDispatch();
    const isReview = mode === 'review' && request?.status === STATUS.pending;
    const [comment, setComment] = useState('');
    const [decisions, setDecisions] = useState({});
    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        setComment('');
        const groups = groupChanges(request?.changes || []);
        const initDecisions = {};
        const initExpanded = {};
        groups.forEach((g) => {
            initDecisions[g.key] = STATUS.approved; // default to approve
            initExpanded[g.key] = true; // everything starts expanded
        });
        setDecisions(initDecisions);
        setExpanded(initExpanded);
    }, [request, open]);

    if (!request) return null;

    const changes = request.changes || [];
    const grouped = groupChanges(changes);

    const setDecision = (key, status) =>
        setDecisions((d) => ({ ...d, [key]: status }));
    const setAll = (status) =>
        setDecisions(Object.fromEntries(grouped.map((g) => [g.key, status])));
    const toggleExpand = (key) =>
        setExpanded((e) => ({ ...e, [key]: !e[key] }));

    // Footer counts fields (not groups) so the reviewer sees the real impact.
    const approvedFieldCount = grouped.reduce(
        (sum, g) => sum + (decisions[g.key] === STATUS.approved ? g.members.length : 0),
        0
    );

    const submitReview = () => {
        // A group decision applies to every field in that group.
        const decisionList = [];
        grouped.forEach((g) =>
            g.members.forEach((m) =>
                decisionList.push({
                    field: m.field,
                    status: decisions[g.key] || STATUS.rejected
                })
            )
        );
        dispatch(
            reviewChangeRequest({
                payload: {
                    id: request.id,
                    decisions: decisionList,
                    comment: comment || undefined
                },
                handleSubmit: () => {
                    onClose();
                    onReviewed && onReviewed();
                }
            })
        );
    };

    return (
        <DialogWrapper open={open} onClose={onClose} fullWidth maxWidth='md'>
            <Box sx={{ textAlign: 'left' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1.5
                    }}
                >
                    <Typography variant='h6'>Change Request Details</Typography>
                    <StatusChip status={request.status} />
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ maxHeight: '62vh', overflowY: 'auto', pr: 0.5 }}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <InfoItem label='Employee' value={request.employeeEmail} />
                        <InfoItem
                            label='Requested On'
                            value={
                                request.createdAt
                                    ? dayjs(request.createdAt).format('MMM DD, YYYY (ddd)')
                                    : 'N/A'
                            }
                        />
                        <InfoItem
                            label='Reviewed By'
                            value={
                                request.updatedBy
                                    ? `${request.updatedBy}${
                                          request.updatedAt
                                              ? ` — ${dayjs(request.updatedAt).format('MMM DD, YYYY')}`
                                              : ''
                                      }`
                                    : null
                            }
                        />
                        {request.note && <InfoItem label='Employee Note' value={request.note} />}
                        {request.comment && (
                            <InfoItem label='Reviewer Comment' value={request.comment} />
                        )}
                    </Grid>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1
                        }}
                    >
                        <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                            Requested Changes ({changes.length})
                        </Typography>
                        {isReview && (
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <Button
                                    color='success'
                                    variant='outlined'
                                    size='small'
                                    onClick={() => setAll(STATUS.approved)}
                                >
                                    Approve All
                                </Button>
                                <Button
                                    color='error'
                                    variant='outlined'
                                    size='small'
                                    onClick={() => setAll(STATUS.rejected)}
                                >
                                    Reject All
                                </Button>
                            </Box>
                        )}
                    </Box>

                    <TableBox>
                        <TableContainer sx={{ boxShadow: 'none' }}>
                            <TableHeaderContainer>
                                <TableCellContainer typography='caption' sx={{ pl: 3, width: '42%' }}>
                                    Section
                                </TableCellContainer>
                                <TableCellContainer typography='caption' sx={{ pl: 2, width: '38%' }}>
                                    Changes
                                </TableCellContainer>
                                <TableCellContainer typography='caption' align='center' sx={{ pr: 3, width: '20%' }}>
                                    {isReview ? 'Decision' : 'Status'}
                                </TableCellContainer>
                            </TableHeaderContainer>
                            <TableBody>
                                {grouped.map((group) => {
                                    const isOpen = !!expanded[group.key];
                                    const isListGroup = group.type === 'list';
                                    const summary = isListGroup
                                        ? group.members[0]?.newValue || 'None'
                                        : `${group.members.length} field${
                                              group.members.length > 1 ? 's' : ''
                                          } changed`;
                                    return (
                                        <Fragment key={group.key}>
                                            <TableRow
                                                onClick={() => toggleExpand(group.key)}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <TableCellContainer sx={{ verticalAlign: 'top', pl: 3 }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            flexWrap: 'wrap'
                                                        }}
                                                    >
                                                        <IconButton
                                                            size='small'
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                toggleExpand(group.key);
                                                            }}
                                                            sx={{
                                                                p: 0.25,
                                                                transform: isOpen ? 'rotate(180deg)' : 'none',
                                                                transition: 'transform 0.2s'
                                                            }}
                                                        >
                                                            <ExpandMoreIcon fontSize='small' />
                                                        </IconButton>
                                                        <Typography variant='body2' sx={{ fontWeight: 600 }}>
                                                            {group.title}
                                                        </Typography>
                                                        {isListGroup && (
                                                            <Chip
                                                                size='small'
                                                                variant='outlined'
                                                                icon={<FormatListBulletedIcon sx={{ fontSize: 13 }} />}
                                                                label='List'
                                                            />
                                                        )}
                                                    </Box>
                                                </TableCellContainer>
                                                <TableCellContainer sx={{ verticalAlign: 'top', pl: 2, color: 'text.secondary' }}>
                                                    {summary}
                                                </TableCellContainer>
                                                <TableCellContainer
                                                    align='center'
                                                    sx={{ verticalAlign: 'top', pr: 3 }}
                                                    onClick={(event) => event.stopPropagation()}
                                                >
                                                    {isReview ? (
                                                        <DecisionToggle
                                                            value={decisions[group.key]}
                                                            onChange={(next) => setDecision(group.key, next)}
                                                        />
                                                    ) : (
                                                        <StatusChip status={groupStatus(group.members)} />
                                                    )}
                                                </TableCellContainer>
                                            </TableRow>
                                            {isOpen && (
                                                <TableRow>
                                                    <TableCellContainer
                                                        colSpan={3}
                                                        sx={{
                                                            px: 3,
                                                            py: 2,
                                                            backgroundColor: 'action.hover'
                                                        }}
                                                    >
                                                        <GroupDetail group={group} />
                                                    </TableCellContainer>
                                                </TableRow>
                                            )}
                                        </Fragment>
                                    );
                                })}
                                {!grouped.length && (
                                    <TableRow>
                                        <TableCellContainer
                                            colSpan={3}
                                            align='center'
                                            sx={{ py: 4, color: 'text.secondary' }}
                                        >
                                            No field changes recorded.
                                        </TableCellContainer>
                                    </TableRow>
                                )}
                            </TableBody>
                        </TableContainer>
                    </TableBox>

                    {isReview && (
                        <TextField
                            fullWidth
                            size='small'
                            multiline
                            minRows={2}
                            sx={{ mt: 2 }}
                            placeholder='Comment (optional — the employee will see this)'
                            value={comment}
                            onChange={(event) => setComment(event.target.value)}
                        />
                    )}
                </Box>

                <Divider sx={{ mt: 2, mb: 2 }} />
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2
                    }}
                >
                    <Typography variant='caption' color='text.secondary'>
                        {isReview
                            ? `${approvedFieldCount} of ${changes.length} field${
                                  changes.length > 1 ? 's' : ''
                              } will be approved`
                            : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {canCancel && request.status === STATUS.pending && (
                            <Button
                                color='error'
                                variant='outlined'
                                onClick={() => onCancel && onCancel(request)}
                            >
                                Withdraw
                            </Button>
                        )}
                        <Button color='tertiary' onClick={onClose}>
                            Close
                        </Button>
                        {isReview && (
                            <Button color='success' onClick={submitReview}>
                                Submit
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>
        </DialogWrapper>
    );
};

export default ChangeRequestDetailsDialog;
